import { useState, useRef, useEffect, KeyboardEvent, DragEvent } from "react";
import { Send, Paperclip, X, FileText, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";

export interface AttachedFile {
  name: string;
  type: string;
  size: number;
  url?: string;
  path?: string;
  /** base64 data URI for images to show preview before upload */
  preview?: string;
}

interface ComposerProps {
  onSend: (content: string, attachments?: AttachedFile[]) => void;
  disabled?: boolean;
}

const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB
const ALLOWED_TYPES = [
  "image/png", "image/jpeg", "image/webp", "image/gif",
  "application/pdf",
  "text/plain", "text/markdown", "text/csv",
  "application/json",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
];

export function Composer({ onSend, disabled }: ComposerProps) {
  const [value, setValue] = useState("");
  const [files, setFiles] = useState<AttachedFile[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height =
        Math.min(textareaRef.current.scrollHeight, 200) + "px";
    }
  }, [value]);

  const handleFiles = async (fileList: FileList | File[]) => {
    const arr = Array.from(fileList).slice(0, 10 - files.length);
    if (arr.length === 0) return;

    const newFiles: AttachedFile[] = [];
    for (const file of arr) {
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`${file.name} is too large (max 20MB)`);
        continue;
      }

      let preview: string | undefined;
      if (file.type.startsWith("image/")) {
        preview = URL.createObjectURL(file);
      }

      newFiles.push({
        name: file.name,
        type: file.type,
        size: file.size,
        preview,
      });
    }

    setFiles((prev) => [...prev, ...newFiles]);
  };

  const removeFile = (idx: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSend = () => {
    const trimmed = value.trim();
    if ((!trimmed && files.length === 0) || disabled) return;
    onSend(trimmed, files.length > 0 ? files : undefined);
    setValue("");
    setFiles([]);
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleDragOver = (e: DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e: DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  };

  return (
    <div className="w-full max-w-3xl mx-auto px-4 pb-4">
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`relative rounded-2xl border bg-[hsl(var(--composer-bg))] shadow-lg shadow-black/20 transition-colors ${
          dragOver
            ? "border-primary bg-primary/5"
            : "border-[hsl(var(--composer-border))] focus-within:border-primary/40"
        }`}
      >
        {/* File previews */}
        {files.length > 0 && (
          <div className="flex flex-wrap gap-2 px-3 pt-3">
            {files.map((f, i) => (
              <div
                key={i}
                className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-secondary border border-border text-xs text-foreground max-w-[200px]"
              >
                {f.type.startsWith("image/") ? (
                  f.preview ? (
                    <img src={f.preview} alt={f.name} className="w-8 h-8 rounded object-cover" />
                  ) : (
                    <ImageIcon className="h-4 w-4 text-muted-foreground shrink-0" />
                  )
                ) : (
                  <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                )}
                <span className="truncate">{f.name}</span>
                <button
                  onClick={() => removeFile(i)}
                  className="shrink-0 p-0.5 rounded hover:bg-muted transition-colors"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Drag overlay */}
        {dragOver && (
          <div className="absolute inset-0 z-10 flex items-center justify-center rounded-2xl bg-primary/10 border-2 border-dashed border-primary pointer-events-none">
            <p className="text-sm font-medium text-primary">Drop files here</p>
          </div>
        )}

        <div className="flex items-end gap-2 p-3">
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={files.length >= 10}
            className="flex-shrink-0 p-2 rounded-xl text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors disabled:opacity-30"
            aria-label="Attach file"
          >
            <Paperclip className="h-5 w-5" />
          </button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            accept={ALLOWED_TYPES.join(",")}
            onChange={(e) => {
              if (e.target.files) handleFiles(e.target.files);
              e.target.value = "";
            }}
          />
          <textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Message Zeus..."
            rows={1}
            className="flex-1 resize-none bg-transparent text-foreground placeholder:text-muted-foreground text-sm leading-relaxed outline-none max-h-[200px] py-2 scrollbar-thin"
            disabled={disabled}
          />
          <button
            onClick={handleSend}
            disabled={(!value.trim() && files.length === 0) || disabled}
            className="flex-shrink-0 p-2 rounded-xl transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed gold-gradient text-primary-foreground hover:opacity-90"
            aria-label="Send message"
          >
            <Send className="h-5 w-5" />
          </button>
        </div>
      </div>

      <p className="text-center text-xs text-muted-foreground mt-2">
        Zeus can make mistakes. Consider checking important information.
      </p>
    </div>
  );
}
